import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import AccountSecurityModal from './AccountSecurityModal';
import { authAPI, clearAuthToken } from '../config/api';
import lightDashboardLogo from '../assets/AppImages/royalty-logo-preloader-light.png';
import darkDashboardLogo from '../assets/AppImages/royalty-logo-preloader-dark.png';

const adminNavItems = [
  { to: '/admin', label: 'Dashboard', icon: 'bi-grid-1x2-fill', end: true },
  { to: '/admin/bookings', label: 'Bookings', icon: 'bi-calendar2-check-fill', permission: 'bookings.manage' },
  { to: '/admin/vehicles', label: 'Vehicles', icon: 'bi-car-front-fill', permission: 'fleet.manage' },
  { to: '/admin/category', label: 'Categories', icon: 'bi-tags-fill', permission: 'categories.manage' },
  { to: '/admin/users', label: 'Users', icon: 'bi-people-fill', permission: 'users.manage' },
  { to: '/admin/access', label: 'Access Control', icon: 'bi-shield-lock-fill', permission: 'users.manage' },
  { to: '/admin/messages', label: 'Messages', icon: 'bi-chat-left-text-fill', permission: 'messages.manage' },
];

const userNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: 'bi-grid-1x2-fill', end: true },
  { to: '/dashboard/bookings', label: 'My Bookings', icon: 'bi-calendar-heart-fill' },
  { to: '/dashboard/wishlist', label: 'Wishlist', icon: 'bi-heart-fill' },
  { to: '/dashboard/checkout', label: 'Checkout', icon: 'bi-credit-card-2-front-fill' },
  { to: '/dashboard/fleet', label: 'Fleet', icon: 'bi-car-front-fill' },
  { to: '/dashboard/support', label: 'Support', icon: 'bi-headset' },
];

function getCurrentTheme() {
  return (
    document.documentElement.getAttribute('data-theme') ||
    localStorage.getItem('theme') ||
    'light'
  );
}

function getDisplayName(user, fallback) {
  if (!user) return fallback;
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.name || user.email || fallback;
}

function getDisplayInitial(user, fallback = 'D') {
  const displayName = getDisplayName(user, fallback);
  return displayName.trim().charAt(0).toUpperCase() || fallback;
}

function canUseAdminNavItem(item, user) {
  if (!item.permission) return true;
  if (!user) return true;
  const role = (user?.role || '').toLowerCase();
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  if (role === 'admin' && !permissions.length) return true;
  return permissions.includes('platform.full_access') || permissions.includes(item.permission);
}

function DashboardSidebar({ variant = 'admin', cartCount = 0, currentUser = null }) {
  const [theme, setTheme] = useState(getCurrentTheme);
  const [accountOpen, setAccountOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [resolvedUser, setResolvedUser] = useState(currentUser);
  const isAdmin = variant === 'admin';
  const logoSrc = theme === 'light' ? lightDashboardLogo : darkDashboardLogo;
  const accountUser = currentUser || resolvedUser;
  const navItems = isAdmin
    ? adminNavItems.filter((item) => canUseAdminNavItem(item, accountUser))
    : userNavItems;
  const profileTitle = isAdmin ? getDisplayName(accountUser, 'DeRoyalty Admin') : getDisplayName(accountUser, 'My Rental Desk');
  const profileSubtitle = isAdmin ? 'Operations Control' : `${cartCount} active booking${cartCount === 1 ? '' : 's'}`;
  const accountInitial = getDisplayInitial(accountUser, isAdmin ? 'A' : 'U');

  useEffect(() => {
    const syncTheme = () => setTheme(getCurrentTheme());
    window.addEventListener('storage', syncTheme);
    window.addEventListener('deroyalty-theme-change', syncTheme);
    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('deroyalty-theme-change', syncTheme);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      setResolvedUser(currentUser);
      return undefined;
    }

    let active = true;
    const currentUserRequest =
      typeof authAPI?.getCurrentUser === 'function'
        ? authAPI.getCurrentUser({ suppressUnauthorizedRedirect: true })
        : null;

    Promise.resolve(currentUserRequest)
      .then((response) => {
        if (active && response?.data?.user) {
          setResolvedUser(response.data.user);
        } else if (active) {
          setResolvedUser(null);
        }
      })
      .catch(() => {
        if (active) setResolvedUser(null);
      });

    return () => {
      active = false;
    };
  }, [currentUser]);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', nextTheme);
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);
    window.dispatchEvent(new CustomEvent('deroyalty-theme-change', {
      detail: { theme: nextTheme },
    }));
  };

  const handleLogout = async () => {
    setAccountOpen(false);
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Dashboard logout error:', error);
    } finally {
      clearAuthToken();
      window.location.href = '/home';
    }
  };

  return (
    <>
      <aside className="admin-sidebar">
        <Link to="/home" className="dashboard-sidebar-brand" aria-label="Back to DeRoyalty home">
          <img className="dashboard-brand-logo" src={logoSrc} alt="DeRoyalty Car Rental" />
        </Link>

        <div className="dashboard-sidebar-profile">
          <div className="dashboard-profile-badge" aria-hidden="true">
            <i className={`bi ${isAdmin ? 'bi-person-gear' : 'bi-person-vcard'}`} />
          </div>
          <div>
            <strong>{profileTitle}</strong>
            <span>{profileSubtitle}</span>
          </div>
        </div>

        <nav className="admin-sidebar-nav" aria-label={isAdmin ? 'Admin dashboard navigation' : 'Customer dashboard navigation'}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              aria-label={item.label}
              title={item.label}
              className={({ isActive }) =>
                `admin-sidebar-link ${isActive ? 'active' : ''}`
              }
            >
              <i className={`bi ${item.icon}`} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <NavLink to="/home" className="admin-sidebar-link" aria-label="Back to site">
            <i className="bi bi-house-fill" />
            <span>Back to Site</span>
          </NavLink>
        </div>
      </aside>

      <header className="dashboard-topbar">
        <div className="dashboard-topbar-copy">
          <span>{isAdmin ? 'Admin workspace' : 'Customer workspace'}</span>
          <strong>{isAdmin ? 'Fleet, bookings, accounts, access, and messages' : 'Bookings, saved vehicles, and rental services'}</strong>
        </div>

        <div className="dashboard-topbar-actions">
          <Link to={isAdmin ? '/admin/bookings' : '/dashboard/fleet'} className="dashboard-topbar-pill">
            <i className={isAdmin ? 'bi bi-calendar-check' : 'bi bi-stars'} />
            {isAdmin ? 'Review bookings' : 'View fleet'}
          </Link>
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <div className="dashboard-account-menu">
            <button
              type="button"
              className={`dashboard-account-button ${accountOpen ? 'active' : ''}`}
              aria-label="Open dashboard account menu"
              aria-expanded={accountOpen}
              onClick={() => setAccountOpen((value) => !value)}
            >
              <span className="dashboard-account-initial" aria-hidden="true">{accountInitial}</span>
            </button>
            {accountOpen && (
              <div className="dashboard-account-dropdown">
                {accountUser && (
                  <div className="dashboard-account-dropdown-profile">
                    <strong>{getDisplayName(accountUser, profileTitle)}</strong>
                    {accountUser.email && <span>{accountUser.email}</span>}
                  </div>
                )}
                <Link to={isAdmin ? '/admin' : '/dashboard'} onClick={() => setAccountOpen(false)}>
                  <i className="bi bi-grid" aria-hidden="true" />
                  <span>Dashboard</span>
                </Link>
                <button type="button" onClick={() => { setAccountOpen(false); setSecurityOpen(true); }}>
                  <i className="bi bi-key-fill" aria-hidden="true" />
                  <span>Change password</span>
                </button>
                <Link to="/home" onClick={() => setAccountOpen(false)}>
                  <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                  <span>Back to site</span>
                </Link>
                <button type="button" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right" aria-hidden="true" />
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <AccountSecurityModal
        isOpen={securityOpen}
        onClose={() => setSecurityOpen(false)}
        accountName={getDisplayName(accountUser, profileTitle)}
      />
    </>
  );
}

export default DashboardSidebar;
